import { Grid, styled } from '@mui/material';
import { RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import PinField from 'react-pin-field';
import useResendUserValidationPhone from '../../../hooks/user/useResendUserValidationPhone';
import useValidateUserPhone from '../../../hooks/user/useValidateUserPhone';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';

type steps = `description` | `validate_phone`;
const RESEND_CODE_TIMEOUT_SECONDS = 30;

type ResendButtonProps = {
  onClick: () => void;
  isVisible: boolean;
  countdown: number;
  setIsVisible: (value: boolean) => void;
  setCountdown: (value: number) => void;
};

const ResendButton = ({ onClick, isVisible, countdown, setIsVisible, setCountdown }: ResendButtonProps) => {
  const [showCoundown, setShowCoundown] = useState<boolean>(false);

  return (
    <Grid container alignItems="center" direction="column">
      <Button
        disabled={!isVisible}
        onClick={() => {
          setShowCoundown(true);
          if (!isVisible) return;
          setIsVisible(false);
          setCountdown(RESEND_CODE_TIMEOUT_SECONDS);
          onClick();
        }}
        intlId="auth.resend_code"
      />
      {showCoundown && (
        <Text
          style={{
            fontSize: `13px`,
            color: RoutalPalette.secondary40,
            margin: 0,
            padding: 0,
            display: isVisible ? `none` : `block`,
            height: `fit-content`,
            fontWeight: 200,
          }}
        >
          {countdown}s
        </Text>
      )}
    </Grid>
  );
};

type ValidatePhoneProps = {
  open: boolean;
  title: string | React.ReactElement;
  description: string | React.ReactElement;
  onCloseDialog: (isValidated?: boolean) => void;
};

export const ValidatePhoneDialog = ({ open, title, description, onCloseDialog }: ValidatePhoneProps) => {
  const [step, setStep] = useState<steps>(`description`);
  const [otp, setOtp] = useState<string>(``);
  const [countdown, setCountdown] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const { run: resendOTP } = useResendUserValidationPhone();
  const { run: validateOTP, success: phoneValidated } = useValidateUserPhone();

  useEffect(() => {
    if (step === `validate_phone` && countdown === 0) {
      resendOTP();
      setCountdown(RESEND_CODE_TIMEOUT_SECONDS);
      setIsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleValidate = () => {
    validateOTP({ otp_code: otp });
  };

  const onResendCode = () => {
    resendOTP();
  };

  const handlePaste: React.ClipboardEventHandler = (event) => {
    const data = event.clipboardData.getData(`text`);
    setOtp(data);
  };

  useEffect(() => {
    if (!isVisible) {
      setTimeout(() => {
        if (countdown > 0) setCountdown(countdown - 1);
      }, 1000);
    }
  }, [isVisible, countdown]);

  useEffect(() => {
    if (countdown === 0) {
      setIsVisible(true);
    }
  }, [countdown]);

  useEffect(() => {
    if (phoneValidated) {
      onCloseDialog(true);
      setStep(`description`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneValidated]);

  useEffect(() => {
    if (otp.length === 6) {
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={() => {
        onCloseDialog();
        setStep(`description`);
      }}
      titleContent={title}
      showFooter={step === `description`}
      footerContent={
        step === `description` ? (
          <Grid container justifyContent="flex-end">
            <Button intlId="continue" onClick={() => setStep(`validate_phone`)} />
          </Grid>
        ) : undefined
      }
    >
      {step === `description` ? (
        <Text>{description}</Text>
      ) : (
        <Grid container direction="column" alignItems="center">
          <Grid item style={{ display: `flex`, justifyContent: `space-evenly`, width: `320px` }}>
            <StyledPinField
              className="pin-field"
              validate="0123456789"
              inputMode="numeric"
              onPaste={handlePaste}
              onChange={setOtp}
              autoComplete="one-time-code"
              length={6}
              autoFocus
            />
          </Grid>

          <VSpacer large />

          <ResendButton
            onClick={() => onResendCode()}
            countdown={countdown}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            setCountdown={setCountdown}
          />
        </Grid>
      )}
    </RoutalDialog>
  );
};

const StyledPinField = styled(PinField)`
  flex-direction: row;
  align-content: space-around;
  justify-content: center;
  flex-wrap: nowrap;
  width: 32px;
  height: 50px;
  font-size: 22px;
  font-weight: 600;
  color: ${RoutalPalette.secondary40};
  user-select: none;
  border-radius: 10px;
  border: 1px solid ${RoutalPalette.primary20};
  text-align: center;

  :focus {
    outline: none;
    box-shadow: inset ${RoutalPalette.primary40} 0px 0px 0px 2px !important;
    border-radius: 10px;
  }

  @media (max-width: 320px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 27px;
  }
  @media (max-width: 270px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 20px;
  }

  :nth-of-type(1) {
    margin-left: 0px;
  }
  :nth-last-of-type(1) {
    margin-right: 0px;
  }
`;
